import axios, { isAxiosError, type InternalAxiosRequestConfig } from 'axios'

/**
 * A single pre-configured axios instance used by every API call.
 *
 * Configure the base URL through the environment (Bun loads `.env`
 * automatically). Production builds inline `API_BASE_URL` via `build.ts`.
 * The dev server only exposes `BUN_PUBLIC_*` (see `bunfig.toml`), so use
 * `BUN_PUBLIC_API_BASE_URL=http://localhost:3001` in `.env` to run against a
 * local backend.
 *
 * Import this instance from the call definitions in `src/api/*` — never call
 * axios directly from components.
 */
export const apiClient = axios.create({
  baseURL: process.env.BUN_PUBLIC_API_BASE_URL?.trim() || process.env.API_BASE_URL?.trim() || 'https://api.halsogruppen.se',
  headers: {
    'Content-Type': 'application/json'
  },
  // Send cookies so the session survives a page reload (drop if you use
  // bearer tokens instead).
  withCredentials: true,
  // Listor skickas som upprepade nycklar (`agent_id=1&agent_id=2`) – backend-schemat avvisar axios standard `agent_id[]`.
  paramsSerializer: { indexes: null }
})

let csrfToken: string | null = null
let hasSetupInterceptors = false

async function fetchCsrfToken(): Promise<string> {
  const { data } = await apiClient.get<{ csrfToken: string }>('/auth/csrf-token')
  return data.csrfToken
}

async function getCsrfToken(): Promise<string> {
  if (csrfToken) {
    return csrfToken
  }

  csrfToken = await fetchCsrfToken()
  return csrfToken
}

export function clearCsrfToken(): void {
  csrfToken = null
}

export function setupInterceptors(onUnauthorized: () => void): void {
  if (hasSetupInterceptors) {
    return
  }

  hasSetupInterceptors = true

  apiClient.interceptors.response.use(
    (response) => response,
    (error: unknown) => {
      if (isAxiosError(error)) {
        const status = error.response?.status

        if (status === 403) {
          clearCsrfToken()
        }

        if (status === 401) {
          onUnauthorized()
        }
      }

      return Promise.reject(error)
    }
  )
}

apiClient.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
  if (config.url?.includes('/auth/csrf-token')) {
    return config
  }

  const token = await getCsrfToken().catch(() => null)

  if (token) {
    config.headers.set('x-csrf-token', token)
  }

  return config
})
